import {
  BirthingCenterAdmissionData,
  EncounterType,
  FullDeliveryRecord,
  getLaborCalendarDates,
  HomebirthOnsetRecord,
  LABOR_CHARTING,
  LaborCharting,
  LaborEntry,
  LaborEntryType,
  LaborIntrapartumTimeLog,
  MinimumDeliveryData,
  NeonatalRecord,
  SubsequentDayNote,
  WithId,
} from '@hb/shared'
import { useAuth } from '@hb/react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { db } from '../../../backend/db/db'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import { useProfile } from '../../../contexts'
import { useAssessmentExams } from '../../../hooks/backend'
import { useSelectedPractice } from '../../../hooks'
import { doc, FieldValue, updateDoc } from 'firebase/firestore'
import { EncounterSelection } from '../../Encounters/types'
import { EncounterOpenState, useEncounterOpenState } from '../../Encounters/useEncounterOpenState'
import { LaborTab, SelectedEntry } from './LaborEntry/types'

export type PregnancyStage = 'prenatal' | 'labor' | 'postpartum'

interface LaborChartingContextValue {
  stage: PregnancyStage
  /** Unix ms delivery timestamp, or null if not yet delivered */
  deliveredOn: number | null
  practiceId: string | null
  activeTab: LaborTab
  setActiveTab: (tab: LaborTab) => void
  isSelectingEntry: boolean
  setIsSelectingEntry: (value: boolean) => void
  selectedEntry: SelectedEntry | null
  handleNewEntryType: (type: LaborEntryType) => void
  selectEntry: (id: string) => void
  deselectEntry: () => void
  handleNewEntry: () => void
  handleCancelEntry: () => void
  handleTabChange: (tab: LaborTab) => void
  /** Labor entries sorted by startedAt descending (newest first) */
  laborEntries: WithId<LaborEntry<LaborEntryType>>[]
  /** Birthing-center admission data, or null */
  admission: BirthingCenterAdmissionData | null
  /** §4.0: Homebirth onset record, or null */
  homebirthOnset: HomebirthOnsetRecord | null
  /** Unix ms onset time — whichever activation path was used */
  laborOnsetAt: number | null
  openExam: EncounterSelection | null
  setOpenExam: (exam: EncounterSelection | null) => void
  // §1.6 / M.2: surface routing (view vs. form) and Amend-flow state for openExam.
  encounterOpenState: EncounterOpenState
  // §6.5: Calendar dates spanned by labor (YYYY-MM-DD, ascending). Day 2+ require
  // documentation confirmation for subsequent-day billing.
  laborCalendarDates: string[]
  subsequentDayNotes: Record<string, SubsequentDayNote>
  confirmSubsequentDay: (date: string, note: SubsequentDayNote) => Promise<void>
  // §4.0: Activate the homebirth labor episode with a confirmed onset time.
  activateHomebirth: (onsetAt: number) => Promise<void>
  // §4.1.1: Record the audit-only Report Birth button tap timestamp.
  // Never surfaced to the provider — internal audit record only.
  reportBirthTapped: () => void
  // §4.1.2: Minimum delivery report data, if saved.
  minimumDeliveryReport: MinimumDeliveryData | null
  // §4.1.2: Save the minimum delivery report.
  saveMinimumDeliveryReport: (data: MinimumDeliveryData) => Promise<void>
  // §4.3: Intrapartum time log (for §5.2 auto-population).
  intrapartumTimeLog: LaborIntrapartumTimeLog | null
  // §5.1: Full delivery record — extended fields beyond §4 minimum.
  fullDeliveryRecord: FullDeliveryRecord | null
  saveFullDeliveryRecord: (data: FullDeliveryRecord) => Promise<void>
  // §5.4: Neonatal record — cord blood, PKU; auto-created on delivery save.
  neonatalRecord: NeonatalRecord | null
  saveNeonatalRecord: (data: NeonatalRecord) => Promise<void>
}

const LaborChartingContext = createContext<LaborChartingContextValue | null>(null)

export const LaborChartingProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeTab, setActiveTab] = useState<LaborTab>('entries')
  const [isSelectingEntry, setIsSelectingEntry] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<SelectedEntry | null>(null)
  const encounterOpenState = useEncounterOpenState()
  const { selection: openExam, open: setOpenExam } = encounterOpenState

  const { assessmentId, selectedAssessment } = useProfile()
  const { authUser } = useAuth()
  const { data: laborChartingData } = useDocument<LaborCharting>(LABOR_CHARTING, assessmentId)

  const { dataArr: admissionEncounters } = useAssessmentExams(
    assessmentId,
    EncounterType.BIRTHING_CENTER_ADMISSION,
  )
  const admissionFromEncounter = useMemo(() => {
    const enc = admissionEncounters?.[0]
    const data = enc?.data as BirthingCenterAdmissionData | undefined
    return data?.admittedAt ? data : null
  }, [admissionEncounters])

  const admission = admissionFromEncounter ?? laborChartingData?.admission ?? null
  const homebirthOnset = laborChartingData?.homebirthOnset ?? null
  const minimumDeliveryReport = laborChartingData?.minimumDeliveryReport ?? null
  const fullDeliveryRecord = laborChartingData?.fullDeliveryRecord ?? null
  const neonatalRecord = laborChartingData?.neonatalRecord ?? null
  const intrapartumTimeLog = laborChartingData?.intrapartumTimeLog ?? null
  const deliveredOn = selectedAssessment?.populated?.deliveredOn ?? null
  const { id: providersAppPracticeId } = useSelectedPractice()

  const practiceId = useMemo(
    () => providersAppPracticeId ?? selectedAssessment?.populated?.midwifeId ?? null,
    [providersAppPracticeId, selectedAssessment?.populated?.midwifeId],
  )

  // §4.0: Labor onset time — from birthing-center admission or homebirth confirmation.
  const laborOnsetAt = useMemo<number | null>(
    () => admission?.admittedAt ?? homebirthOnset?.onsetAt ?? null,
    [admission, homebirthOnset],
  )

  // §4.0: Stage is 'labor' when EITHER the birthing-center admission OR homebirth onset is set.
  const stage = useMemo<PregnancyStage>(() => {
    if (deliveredOn && deliveredOn !== Infinity) return 'postpartum'
    if (admission || homebirthOnset) return 'labor'
    return 'prenatal'
  }, [admission, homebirthOnset, deliveredOn])

  const laborEntries = useMemo(
    () =>
      Object.entries(laborChartingData?.entries ?? {})
        .sort((a, b) => b[1].startedAt - a[1].startedAt)
        .map(e => ({ ...e[1], id: e[0] })),
    [laborChartingData],
  )

  // §6.5: Calendar dates spanned by labor entries (ascending).
  const laborCalendarDates = useMemo(
    () => getLaborCalendarDates(laborEntries, laborOnsetAt ?? undefined),
    [laborEntries, laborOnsetAt],
  )

  const subsequentDayNotes = useMemo(
    () => laborChartingData?.subsequentDayNotes ?? {},
    [laborChartingData],
  )

  const confirmSubsequentDay = useCallback(
    async (date: string, note: SubsequentDayNote) => {
      if (!assessmentId) return
      await updateDoc(doc(db, LABOR_CHARTING, assessmentId), {
        [`subsequentDayNotes.${date}`]: note,
      }).catch(console.error)
    },
    [assessmentId],
  )

  // §4.0: Write the homebirth onset record to Firestore, activating the labor module.
  const activateHomebirth = useCallback(
    async (onsetAt: number) => {
      if (!assessmentId) return
      const record: HomebirthOnsetRecord = {
        onsetAt,
        confirmedBy: authUser?.uid ?? 'unknown',
        confirmedAt: Date.now(),
      }
      await updateDoc(doc(db, LABOR_CHARTING, assessmentId), {
        homebirthOnset: record,
      }).catch(console.error)
    },
    [assessmentId, authUser?.uid],
  )

  // §4.1.1: Write the audit-only Report Birth tap timestamp.
  // This field is never shown to any user — billing audit record only.
  const reportBirthTapped = useCallback(() => {
    if (!assessmentId) return
    updateDoc(doc(db, LABOR_CHARTING, assessmentId), {
      reportBirthTappedAt: Date.now(),
    }).catch(console.error)
  }, [assessmentId])

  // §4.1.2: Persist the minimum delivery report.
  const saveMinimumDeliveryReport = useCallback(
    async (data: MinimumDeliveryData) => {
      if (!assessmentId) return
      await updateDoc(doc(db, LABOR_CHARTING, assessmentId), {
        minimumDeliveryReport: data,
      })
    },
    [assessmentId],
  )

  // §5.1: Persist the full delivery record. Auto-creates neonatalRecord stub if not present.
  const saveFullDeliveryRecord = useCallback(
    async (data: FullDeliveryRecord) => {
      if (!assessmentId) return
      const update: Partial<LaborCharting> = { fullDeliveryRecord: data }
      if (!neonatalRecord) {
        update.neonatalRecord = { createdAt: Date.now() }
      }
      await updateDoc(
        doc(db, LABOR_CHARTING, assessmentId),
        update as Record<string, FieldValue | Partial<unknown>>,
      )
    },
    [assessmentId, neonatalRecord],
  )

  // §5.4: Persist neonatal record (cord blood, PKU, etc.).
  const saveNeonatalRecord = useCallback(
    async (data: NeonatalRecord) => {
      if (!assessmentId) return
      await updateDoc(doc(db, LABOR_CHARTING, assessmentId), {
        neonatalRecord: data,
      })
    },
    [assessmentId],
  )

  const value = useMemo<LaborChartingContextValue>(
    () => ({
      stage,
      deliveredOn: deliveredOn && deliveredOn !== Infinity ? deliveredOn : null,
      activeTab,
      setActiveTab,
      isSelectingEntry,
      setIsSelectingEntry,
      selectedEntry,
      laborEntries,
      admission,
      homebirthOnset,
      laborOnsetAt,
      minimumDeliveryReport,
      intrapartumTimeLog,
      fullDeliveryRecord,
      saveFullDeliveryRecord,
      neonatalRecord,
      saveNeonatalRecord,
      practiceId,
      openExam,
      setOpenExam,
      encounterOpenState,
      laborCalendarDates,
      subsequentDayNotes,
      confirmSubsequentDay,
      activateHomebirth,
      reportBirthTapped,
      saveMinimumDeliveryReport,
      deselectEntry: () => setSelectedEntry(null),
      handleNewEntryType: (type: LaborEntryType) => {
        setIsSelectingEntry(false)
        setSelectedEntry({ type, startedAt: Date.now() })
      },
      selectEntry: (id: string) => {
        const entry = laborChartingData?.entries?.[id]
        if (!entry) return
        setSelectedEntry({ type: entry.type, existing: { id, entry } })
      },
      handleNewEntry: () => {
        setSelectedEntry(null)
        setIsSelectingEntry(true)
      },
      handleCancelEntry: () => {
        setIsSelectingEntry(false)
        setSelectedEntry(null)
      },
      handleTabChange: (tab: LaborTab) => {
        setActiveTab(tab)
        setIsSelectingEntry(false)
        setSelectedEntry(null)
      },
    }),
    [
      stage,
      activeTab,
      isSelectingEntry,
      selectedEntry,
      laborEntries,
      admission,
      homebirthOnset,
      laborOnsetAt,
      minimumDeliveryReport,
      intrapartumTimeLog,
      fullDeliveryRecord,
      saveFullDeliveryRecord,
      neonatalRecord,
      saveNeonatalRecord,
      laborChartingData,
      openExam,
      setOpenExam,
      encounterOpenState,
      practiceId,
      deliveredOn,
      laborCalendarDates,
      subsequentDayNotes,
      confirmSubsequentDay,
      activateHomebirth,
      reportBirthTapped,
      saveMinimumDeliveryReport,
    ],
  )

  return <LaborChartingContext.Provider value={value}>{children}</LaborChartingContext.Provider>
}

export const useLaborCharting = () => {
  const ctx = useContext(LaborChartingContext)
  if (!ctx) throw new Error('useLaborCharting must be used within LaborChartingProvider')
  return ctx
}
